import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Active, CollisionDetection, DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './components/SortableItem';
import { SortableOverlay } from './components/SortableOverlay';
import { VirtualFixedList } from '@shared/components/VirtualScroll';
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
interface BaseItem {
  id: UniqueIdentifier;
}

interface SortableListProps<T extends BaseItem> {
  items: T[];
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number, isDragActive?: boolean) => React.ReactNode;
  hasDragHandle?: boolean;
  handleDragStart?: (activeId: string) => void;
  handleDragEnd?: (dragData: { fromIndex: number; toIndex: number; items: T[]; reorderedItem: T | undefined }) => void;
  handleDragCancel?: () => void;
  renderVirtualList?: boolean;
  itemSize?: number;
  adjustOverlayScale?: boolean;
  customStyle?: React.CSSProperties;
  dragPlaceholderOpacity?: number;
  collisionDetection?: CollisionDetection;
  disabled?: boolean;
}

export const SortableList = <T extends BaseItem>(props: SortableListProps<T>) => {
  const {
    items,
    renderItem,
    onChange,
    hasDragHandle = false,
    handleDragStart = () => {},
    handleDragEnd = () => {},
    handleDragCancel = () => {},
    renderVirtualList = false,
    itemSize = 0,
    adjustOverlayScale = true,
    customStyle = {},
    dragPlaceholderOpacity,
    collisionDetection,
    disabled,
  } = props;

  const [active, setActive] = useState<Active | null>(null);

  const activeItem = useMemo(() => items.find(item => item.id === active?.id), [active, items]);
  const activeItemIndex = useMemo(() => items.findIndex(item => item.id === active?.id), [active, items]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActive(active);
    handleDragStart(String(active.id));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    let activeIndex = -1;
    let overIndex = -1;
    let updatedItems: T[] = [];
    const selectedItem = activeItem;
    if (over && active.id !== over?.id) {
      activeIndex = items.findIndex(({ id }) => id === active.id);
      overIndex = items.findIndex(({ id }) => id === over.id);
      updatedItems = arrayMove(items, activeIndex, overIndex);
      onChange(updatedItems);
    }
    setActive(null);
    handleDragEnd({ fromIndex: activeIndex, toIndex: overIndex, items: updatedItems, reorderedItem: selectedItem });
  };

  const onDragCancel = () => {
    setActive(null);
    handleDragCancel();
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      collisionDetection={collisionDetection}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
      >
        {renderVirtualList ? (
          <VirtualFixedList
            itemData={items}
            itemCount={items.length}
            overscanCount={3}
            itemSize={() => itemSize}
          >
            {({ data, index, style }) => (
              <div
                style={style}
                className={`virtual-wrapper-${data[index].id}`}
              >
                <SortableItem
                  key={data[index].id}
                  id={data[index].id}
                  hasDragHandle={hasDragHandle}
                  customStyle={customStyle}
                  dragPlaceholderOpacity={dragPlaceholderOpacity}
                  disabled={disabled}
                >
                  {renderItem(data[index], index, active?.id === data[index].id)}
                </SortableItem>
              </div>
            )}
          </VirtualFixedList>
        ) : (
          items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              hasDragHandle={hasDragHandle}
              customStyle={customStyle}
              dragPlaceholderOpacity={dragPlaceholderOpacity}
              disabled={disabled}
            >
              {renderItem(item, index, active?.id === item.id)}
            </SortableItem>
          ))
        )}
      </SortableContext>
      {createPortal(<SortableOverlay adjustScale={adjustOverlayScale}>{activeItem ? renderItem(activeItem, activeItemIndex, true) : null}</SortableOverlay>, document.body)}
    </DndContext>
  );
};
